import { useQueries, useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { Fragment, useMemo, useState } from 'react'
import {
  getMissionKillfeed,
  getMissionPlayers,
  getMissionStats,
  getMissionTimeseries,
  getMissionWeapons,
} from '@/lib/api'
import { PageMotion } from '@/components/Motion'
import {
  EmptyState,
  ErrorBlock,
  LoadingBlock,
  PageHeader,
  Panel,
  StatStrip,
} from '@/components/ui'
import { cn, formatDistance, formatNum, formatPct, sideColor } from '@/lib/utils'
import type { Kill, MissionPlayer } from '@/types'
import { ChevronDown, ExternalLink } from 'lucide-react'
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  BarChart,
  Bar,
} from 'recharts'

const SIDE_COLORS: Record<string, string> = {
  WEST: '#5b8fb9',
  EAST: '#c07070',
  GUER: '#6a9a6a',
  CIV: '#b89a5a',
  UNKNOWN: '#a3a3a3',
}

const chartTooltip = {
  background: '#222222',
  border: '1px solid #2e2e2e',
  borderRadius: 6,
  fontSize: 12,
  color: '#e5e5e5',
}

const ACCENT = '#8b9a7c'
const GRID = '#2e2e2e'
const MUTED = '#a3a3a3'
const INK = '#e5e5e5'

export default function MissionDetail() {
  const { id = '' } = useParams()
  const [killPage, setKillPage] = useState(1)
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())

  function expandKey(playerId: number, section: 'kills' | 'deaths') {
    return `${playerId}:${section}`
  }

  function toggleExpand(playerId: number, section: 'kills' | 'deaths') {
    const key = expandKey(playerId, section)
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const results = useQueries({
    queries: [
      { queryKey: ['mission-stats', id], queryFn: () => getMissionStats(id), enabled: !!id },
      { queryKey: ['mission-players', id], queryFn: () => getMissionPlayers(id), enabled: !!id },
      {
        queryKey: ['mission-ts', id],
        queryFn: () => getMissionTimeseries(id),
        enabled: !!id,
      },
      {
        queryKey: ['mission-weapons', id],
        queryFn: () => getMissionWeapons(id),
        enabled: !!id,
      },
    ],
  })

  const killfeed = useQuery({
    queryKey: ['mission-killfeed', id, killPage],
    queryFn: () => getMissionKillfeed(id, killPage, 40),
    enabled: !!id,
  })

  const [stats, players, timeseries, weapons] = results
  const loading = results.some((r) => r.isLoading)
  const failed = results.some((r) => r.error)

  const aliveChart = useMemo(() => {
    if (!timeseries.data) return []
    return timeseries.data.alive.map((p) => ({
      t: p.missionTime,
      minutes: p.minutes,
      alive: p.alive,
      ...p.sides,
    }))
  }, [timeseries.data])

  const fpsChart = useMemo(() => {
    if (!timeseries.data) return []
    return timeseries.data.fps.map((p, i) => ({ i, fps: p.fps, t: p.timeUTC }))
  }, [timeseries.data])

  const bySide = useMemo(() => {
    const map = new Map<string, typeof players.data>()
    for (const p of players.data ?? []) {
      const side = p.side || 'UNKNOWN'
      if (!map.has(side)) map.set(side, [])
      map.get(side)!.push(p)
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b))
  }, [players.data])

  if (loading) return <LoadingBlock />
  if (failed || !stats.data) return <ErrorBlock message="Mission not found or failed to load" />

  const m = stats.data.mission
  const sides = timeseries.data?.presentSides ?? []

  return (
    <PageMotion className="space-y-8">
      <PageHeader
        breadcrumb={
          <p className="text-xs uppercase tracking-[0.14em] text-ink-faint">
            <Link to="/missions" className="hover:text-accent-text transition-colors">
              Missions
            </Link>
            <span className="mx-2">/</span>#{m.id}
          </p>
        }
        title={m.missionName || `Mission #${m.id}`}
        subtitle={[m.worldname, m.missionAuthor, m.missionType].filter(Boolean).join(' · ')}
        action={
          m.ocapLink ? (
            <a
              href={m.ocapLink}
              target="_blank"
              rel="noreferrer"
              className="btn-ghost self-start"
            >
              <ExternalLink size={14} /> OCAP
            </a>
          ) : undefined
        }
      />

      <StatStrip
        items={[
          { label: 'Kills', value: stats.data.totalKills },
          { label: 'Players', value: stats.data.playerCount },
          { label: 'Avg FPS', value: formatNum(stats.data.avgFPS, 1) },
          { label: 'Victory', value: m.victory || '—' },
        ]}
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Players alive">
          {aliveChart.length === 0 ? (
            <EmptyState message="No timeline data" />
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={aliveChart}>
                  <CartesianGrid stroke={GRID} strokeDasharray="3 3" />
                  <XAxis dataKey="t" tick={{ fill: MUTED, fontSize: 10 }} />
                  <YAxis tick={{ fill: MUTED, fontSize: 10 }} allowDecimals={false} />
                  <Tooltip contentStyle={chartTooltip} />
                  <Legend />
                  <Line
                    type="stepAfter"
                    dataKey="alive"
                    stroke={INK}
                    strokeWidth={1.5}
                    dot={false}
                    name="Total"
                  />
                  {sides.map((s) => (
                    <Line
                      key={s}
                      type="stepAfter"
                      dataKey={s}
                      stroke={SIDE_COLORS[s] || MUTED}
                      strokeWidth={1.5}
                      dot={false}
                      name={s}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>

        <Panel title="Server FPS">
          {fpsChart.length === 0 ? (
            <EmptyState message="No FPS samples" />
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={fpsChart}>
                  <CartesianGrid stroke={GRID} strokeDasharray="3 3" />
                  <XAxis dataKey="i" hide />
                  <YAxis tick={{ fill: MUTED, fontSize: 10 }} domain={['auto', 'auto']} />
                  <Tooltip
                    contentStyle={chartTooltip}
                    labelFormatter={(i) => fpsChart[Number(i)]?.t ?? ''}
                  />
                  <Line type="monotone" dataKey="fps" stroke={ACCENT} strokeWidth={1.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Top players" flush>
          <div className="table-wrap border-0 rounded-none">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Player</th>
                  <th>Side</th>
                  <th>K</th>
                  <th>D</th>
                  <th>Acc</th>
                </tr>
              </thead>
              <tbody>
                {(stats.data.topPlayers ?? []).map((p) => (
                  <tr key={p.playerId}>
                    <td>
                      <Link to={`/players/${p.playerId}`} className="link-accent font-medium">
                        {p.name}
                      </Link>
                    </td>
                    <td className={sideColor(p.side || '')}>{p.side || '—'}</td>
                    <td className="font-mono text-accent-text">{p.kills}</td>
                    <td className="font-mono">{p.deaths ?? 0}</td>
                    <td className="font-mono text-xs">
                      {p.shots ? formatPct(((p.hits ?? 0) / p.shots) * 100) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel title="Weapons">
          {(weapons.data ?? []).length === 0 ? (
            <EmptyState message="No weapons" />
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={(weapons.data ?? []).slice(0, 8).map((w) => ({
                    name: w.weapon.length > 14 ? w.weapon.slice(0, 12) + '…' : w.weapon,
                    count: w.count,
                  }))}
                  layout="vertical"
                  margin={{ left: 4, right: 8 }}
                >
                  <XAxis type="number" hide />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={100}
                    tick={{ fill: MUTED, fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip contentStyle={chartTooltip} />
                  <Bar dataKey="count" fill={ACCENT} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>
      </div>

      <Panel title="Roster">
        {bySide.length === 0 ? (
          <EmptyState message="No players" />
        ) : (
          <div className="space-y-6">
            {bySide.map(([side, list]) => {
              const bySquad = new Map<string, typeof list>()
              for (const p of list ?? []) {
                const sq = p.squad || 'Ungrouped'
                if (!bySquad.has(sq)) bySquad.set(sq, [])
                bySquad.get(sq)!.push(p)
              }
              return (
                <div key={side}>
                  <h3 className={`text-sm font-semibold mb-2 ${sideColor(side)}`}>{side}</h3>
                  {[...bySquad.entries()].map(([squad, members]) => (
                    <div key={squad} className="mb-3">
                      <div className="text-xs text-ink-faint mb-1.5">{squad}</div>
                      <div className="table-wrap">
                        <table className="data-table table-fixed w-full min-w-[640px]">
                          <colgroup>
                            <col className="w-[18%]" />
                            <col className="w-[22%]" />
                            <col className="w-[10%]" />
                            <col className="w-[10%]" />
                            <col className="w-[9%]" />
                            <col className="w-[9%]" />
                            <col className="w-[9%]" />
                            <col className="w-[13%]" />
                          </colgroup>
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Role</th>
                              <th>Kills</th>
                              <th>Deaths</th>
                              <th>Shots</th>
                              <th>Hits</th>
                              <th>Acc</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(members ?? []).map((p) => (
                              <RosterRow
                                key={p.id}
                                player={p}
                                openKills={expanded.has(expandKey(p.id, 'kills'))}
                                openDeaths={expanded.has(expandKey(p.id, 'deaths'))}
                                onToggle={(section) => toggleExpand(p.id, section)}
                              />
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )
            })}
          </div>
        )}
      </Panel>

      <Panel title="Killfeed" flush>
        {killfeed.isLoading ? (
          <LoadingBlock label="Loading killfeed…" />
        ) : (
          <>
            <div className="table-wrap border-0 rounded-none">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Killer</th>
                    <th>Weapon</th>
                    <th>Victim</th>
                    <th>Dist</th>
                  </tr>
                </thead>
                <tbody>
                  {(killfeed.data?.data ?? []).map((k) => (
                    <tr key={k.id}>
                      <td className="font-mono text-xs text-ink-faint">{k.time}</td>
                      <td>
                        <Link to={`/players/${k.killerId}`} className="link-accent">
                          {k.killerName}
                        </Link>
                      </td>
                      <td className="text-xs text-ink-muted">{k.weapon}</td>
                      <td>
                        <Link to={`/players/${k.victimId}`} className="link-accent">
                          {k.victimName}
                        </Link>
                      </td>
                      <td className="font-mono text-xs">{formatDistance(k.distance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {killfeed.data && killfeed.data.totalPages > 1 && (
              <div className="flex justify-between items-center px-4 py-3 border-t border-line text-sm">
                <span className="text-ink-faint">
                  Page {killfeed.data.page} / {killfeed.data.totalPages}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="btn-ghost"
                    disabled={killPage <= 1}
                    onClick={() => setKillPage((p) => p - 1)}
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    className="btn-ghost"
                    disabled={killPage >= killfeed.data.totalPages}
                    onClick={() => setKillPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </Panel>
    </PageMotion>
  )
}

function RosterRow({
  player: p,
  openKills,
  openDeaths,
  onToggle,
}: {
  player: MissionPlayer
  openKills: boolean
  openDeaths: boolean
  onToggle: (section: 'kills' | 'deaths') => void
}) {
  const hasKills = (p.killDetails?.length ?? 0) > 0
  const hasDeaths = (p.deathDetails?.length ?? 0) > 0

  return (
    <Fragment>
      <tr>
        <td className="truncate">
          <Link to={`/players/${p.globalPlayerId}`} className="link-accent font-medium">
            {p.name}
          </Link>
        </td>
        <td className="text-xs text-ink-muted truncate" title={p.role || undefined}>
          {p.role || '—'}
        </td>
        <td>
          <ExpandStat
            value={p.kills}
            open={openKills}
            enabled={hasKills}
            onToggle={() => onToggle('kills')}
            accent
          />
        </td>
        <td>
          <ExpandStat
            value={p.deaths}
            open={openDeaths}
            enabled={hasDeaths}
            onToggle={() => onToggle('deaths')}
          />
        </td>
        <td className="font-mono">{p.shots}</td>
        <td className="font-mono">{p.hits}</td>
        <td className="font-mono text-xs">
          {p.shots ? formatPct((p.hits / p.shots) * 100) : '—'}
        </td>
        <td>
          <span className={p.isDead ? 'text-red-400/90' : 'text-accent-text'}>
            {p.isDead ? 'KIA' : 'Alive'}
          </span>
        </td>
      </tr>
      {openKills && hasKills && (
        <tr>
          <td colSpan={8} className="!whitespace-normal !p-0 bg-canvas-overlay">
            <div className="p-4">
              <KillDeathList
                title="Kills"
                empty="No kills"
                items={p.killDetails ?? []}
                mode="kills"
              />
            </div>
          </td>
        </tr>
      )}
      {openDeaths && hasDeaths && (
        <tr>
          <td colSpan={8} className="!whitespace-normal !p-0 bg-canvas-overlay">
            <div className="p-4">
              <KillDeathList
                title="Deaths"
                empty="No deaths"
                items={p.deathDetails ?? []}
                mode="deaths"
              />
            </div>
          </td>
        </tr>
      )}
    </Fragment>
  )
}

function ExpandStat({
  value,
  open,
  enabled,
  onToggle,
  accent,
}: {
  value: number
  open: boolean
  enabled: boolean
  onToggle: () => void
  accent?: boolean
}) {
  if (!enabled) {
    return (
      <span className={cn('font-mono', accent && 'text-accent-text')}>{value}</span>
    )
  }

  return (
    <button
      type="button"
      className={cn(
        'inline-flex items-center gap-1 font-mono rounded-control px-1.5 py-0.5 -mx-1.5 transition-colors hover:bg-canvas-overlay',
        accent ? 'text-accent-text' : 'text-ink-muted',
        open && 'bg-canvas-overlay',
      )}
      onClick={onToggle}
      aria-expanded={open}
    >
      {value}
      <ChevronDown
        size={12}
        className={cn('text-ink-faint transition-transform', open && 'rotate-180')}
      />
    </button>
  )
}

function KillDeathList({
  title,
  empty,
  items,
  mode,
}: {
  title: string
  empty: string
  items: Kill[]
  mode: 'kills' | 'deaths'
}) {
  return (
    <div>
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-faint">
        {title}
        {items.length > 0 ? (
          <span className="ml-1.5 font-mono text-ink-muted normal-case tracking-normal">
            ({items.length})
          </span>
        ) : null}
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-ink-faint">{empty}</p>
      ) : (
        <ul className="space-y-1.5">
          {items.map((k) => (
            <li
              key={k.id}
              className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs text-ink-muted"
            >
              <span className="font-mono text-ink-faint shrink-0">{k.time || '—'}</span>
              {mode === 'kills' ? (
                <>
                  <span className="text-ink-faint">→</span>
                  <Link
                    to={`/players/${k.victimId}`}
                    className="link-accent font-medium"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {k.victimName || `#${k.victimId}`}
                  </Link>
                </>
              ) : (
                <>
                  <span className="text-ink-faint">by</span>
                  <Link
                    to={`/players/${k.killerId}`}
                    className="link-accent font-medium"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {k.killerName || `#${k.killerId}`}
                  </Link>
                </>
              )}
              <span className="text-ink-faint">·</span>
              <span>{k.weapon || '—'}</span>
              <span className="text-ink-faint">·</span>
              <span className="font-mono">{formatDistance(k.distance)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
